import React, { useState, useEffect, useRef } from "react";
import { Flashbar, FlashbarProps, Modal, Button } from "@amzn/awsui-components-react";
import { jsxParser } from "src/utilities/jsxParser";
import { PulseSurveyNotificationProps } from "./PulseSurveyNotificationTypes";
import { convertUrlsToLinks } from "../utils/generalUtils";
import { PulseWidgetWrapper as PulseWidget } from "src/generic-components/PulseWidget/PulseWidget";
import { AutoPopupDelayTypes } from "./PulseSurveyNotificationConstants";

// App load time is captured once when this module is first imported.
// This gives us the time the SPA was loaded/refreshed, which is used for session time calculations.
const appLoadTimestamp: number = Date.now();
const getAppLoadTime = (): number => appLoadTimestamp;

export default (props: PulseSurveyNotificationProps) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [delayedAutoPopup, setDelayedAutoPopup] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (props.autoPopup && props.surveyId) {
            const delayType = props.autoPopupDelayType;
            const delaySeconds = props.autoPopupDelaySeconds || 0;

            if (!delayType || delayType === AutoPopupDelayTypes.NONE || delaySeconds <= 0) {
                // No delay - show immediately
                setDelayedAutoPopup(true);
            } else if (delayType === AutoPopupDelayTypes.TIME_ON_PAGE) {
                // Delay based on time spent on the current page
                timerRef.current = setTimeout(() => {
                    setDelayedAutoPopup(true);
                }, delaySeconds * 1000);
            } else if (delayType === AutoPopupDelayTypes.SESSION_TIME) {
                // Delay based on total session time on the website
                const sessionStart = getAppLoadTime();
                const elapsedSeconds = (Date.now() - sessionStart) / 1000;
                const remainingDelay = Math.max(0, delaySeconds - elapsedSeconds);

                if (remainingDelay <= 0) {
                    // Already past the delay threshold
                    setDelayedAutoPopup(true);
                } else {
                    timerRef.current = setTimeout(() => {
                        setDelayedAutoPopup(true);
                    }, remainingDelay * 1000);
                }
            }
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [props.autoPopup, props.autoPopupDelayType, props.autoPopupDelaySeconds]);

    useEffect(() => {
        if (delayedAutoPopup && props.surveyId) {
            setIsModalVisible(true);
            if (props.onAutoPopupShown && props.notificationId) {
                props.onAutoPopupShown(props.notificationId);
            }
        }
    }, [delayedAutoPopup]);
    const processedMessage = convertUrlsToLinks(props.notificationText);

    const handleOpenModal = () => {
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
    };

    return (
        <>
            {!props.hideBanner && (
                <div className={props.options?.roundedCorners ? "" : "flashbar-container"}>
                    <Flashbar
                        items={[{
                            type: props.notificationDisplayType as FlashbarProps.Type,
                            content: jsxParser(processedMessage),
                            dismissible: !!props.options?.onDismiss,
                            onDismiss: props.options?.onDismiss,
                            id: props.surveyId,
                            action: props.surveyId ? (
                                <Button variant="normal" onClick={handleOpenModal}>
                                    Take Survey
                                </Button>
                            ) : undefined,
                        }]}
                    />
                </div>
            )}

            {props.surveyId && isModalVisible && (
                <Modal
                    visible={isModalVisible}
                    onDismiss={handleCloseModal}
                    header="Survey"
                    size="max"
                >
                    <PulseWidget
                        friendlyId={props.surveyId}
                        formType="Survey"
                        displayMode="Inline"
                        onClose={handleCloseModal}
                        open={isModalVisible}
                        onOpenChange={setIsModalVisible}
                        requiresAuth={false}
                    />
                </Modal>
            )}
        </>
    );
};
